import type {
  OrderExportColumnKey,
  OrderExportLineDTO,
} from "@/modules/orders/domain/contracts";
import {
  ORDER_EXPORT_COLUMN_ORDER,
  ORDER_EXPORT_HEADER_LO,
} from "@/modules/orders/domain/contracts";

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function cellForColumn(
  line: OrderExportLineDTO,
  col: OrderExportColumnKey,
): string | number {
  const orderLevelOnlyDuplicatedBlank =
    col === "orderCode" ||
    col === "shippingAddress" ||
    col === "phone" ||
    col === "customerName";
  if (orderLevelOnlyDuplicatedBlank && !line.isFirstLineOfOrder) {
    return "";
  }
  switch (col) {
    case "orderCode":
      return line.orderCode;
    case "lineItems":
      return line.lineDescription;
    case "quantity":
      return line.quantity;
    case "unitPrice":
      return line.unitPrice;
    case "lineAmount":
      return line.lineAmount;
    case "paymentCod":
      return line.paymentCod;
    case "paymentOnline":
      return line.paymentOnline;
    case "shippingAddress":
      return line.shippingAddress;
    case "phone":
      return line.phone;
    case "customerName":
      return line.customerName;
    default:
      return "";
  }
}

const FOOTER_ROW_LABEL = "ລວມ";

const FOOTER_LABEL_PREFERRED_COLS: OrderExportColumnKey[] = [
  "orderCode",
  "lineItems",
  "shippingAddress",
  "phone",
  "customerName",
];

/** ສົ່ງອອກຕາມຄໍລຳທີ່ເລືອກ — ເລກອໍເດີ້/ທີ່ຢູ່/ເບີ/ຊື່ວ່າງໃນແຖວຖັດໄປຂອງອໍເດີ້ດຽວກັນ; ແຖວທ້າຍ ລວມ */
export function buildOrderExportCsv(
  lines: OrderExportLineDTO[],
  columns: OrderExportColumnKey[],
): string {
  const orderedCols = ORDER_EXPORT_COLUMN_ORDER.filter((c) =>
    columns.includes(c),
  );
  const headerLine = orderedCols
    .map((c) => ORDER_EXPORT_HEADER_LO[c])
    .join(",");
  const dataLines = lines.map((line) =>
    orderedCols.map((c) => escapeCsvCell(cellForColumn(line, c))).join(","),
  );

  const footerLabelCol = orderedCols.find((c) =>
    FOOTER_LABEL_PREFERRED_COLS.includes(c),
  );
  const totalQty = lines.reduce((s, l) => s + l.quantity, 0);
  const totalLineAmountSum = lines.reduce((s, l) => s + l.lineAmount, 0);
  const totalCod = lines.reduce((s, l) => s + l.paymentCod, 0);
  const totalOnline = lines.reduce((s, l) => s + l.paymentOnline, 0);

  const footerCells = orderedCols.map((c) => {
    if (c === "quantity") return escapeCsvCell(totalQty);
    if (c === "unitPrice") return escapeCsvCell("");
    if (c === "lineAmount") return escapeCsvCell(totalLineAmountSum);
    if (c === "paymentCod") return escapeCsvCell(totalCod);
    if (c === "paymentOnline") return escapeCsvCell(totalOnline);
    if (footerLabelCol !== undefined && c === footerLabelCol) {
      return escapeCsvCell(FOOTER_ROW_LABEL);
    }
    return escapeCsvCell("");
  });
  const footerLine = footerCells.join(",");

  return `\uFEFF${[headerLine, ...dataLines, footerLine].join("\r\n")}`;
}

export function downloadOrdersCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ordersExportFilename(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `orders-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}.csv`;
}
